import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  clearPersistedSession,
  clearSessionIfExpired,
  loadPersistedSession,
  persistSession,
} from '@/auth/authSession';
import {
  consumeSessionExpiredNotice,
  SESSION_EXPIRED_MESSAGE,
} from '@/auth/sessionNotice';
import { isBearerExpired } from '@/auth/core/isBearerExpired';
import { buildLoginBody } from '@/auth/core/buildLoginBody';
import { buildKincodeGoogleCredential } from '@/auth/core/buildKincodeGoogleCredential';
import type { KincodeGoogleCredential } from '@/auth/core/googleSsoTypes';
import { postGoogleSso } from '@/auth/core/postGoogleSso';
import { postLogin } from '@/auth/core/postLogin';
import { postLogout } from '@/auth/core/postLogout';
import { validateLoginForm } from '@/auth/core/validateLoginForm';
import type { AuthSession, LoginError } from '@/auth/types';
import { Configs } from '@/config';

interface AuthContextValue {
  session: AuthSession | null;
  isAuthenticated: boolean;
  mfaPending: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  /** One-time message for the login screen (e.g. after session expiry). */
  loginNotice: string | null;
  login: (
    username: string,
    password: string
  ) => Promise<{ success: boolean; error?: LoginError | undefined }>;
  loginWithGoogleAccessToken: (
    accessToken: string
  ) => Promise<
    | { success: true }
    | { success: false; error: LoginError }
    | { success: false; notOnboarded: true; message: string }
  >;
  loginWithGoogleCredential: (
    credential: KincodeGoogleCredential
  ) => Promise<
    | { success: true }
    | { success: false; error: LoginError }
    | { success: false; notOnboarded: true; message: string }
  >;
  completeMfaStub: () => void;
  clearLoginNotice: () => void;
  expireSession: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provides auth session state for the agents app.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => loadPersistedSession());
  const [loginNotice, setLoginNotice] = useState<string | null>(() =>
    consumeSessionExpiredNotice() ? SESSION_EXPIRED_MESSAGE : null
  );
  const [mfaPending, setMfaPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const applySession = useCallback((next: AuthSession | null) => {
    setSession(next);
    if (next) {
      persistSession(next);
    } else {
      clearPersistedSession();
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const validation = validateLoginForm(username, password);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          code: 'validation' as const,
          message: validation.missingFields
            ? 'Email and password are required.'
            : 'Enter a valid email address.',
        },
      };
    }

    setIsLoading(true);
    setMfaPending(false);

    try {
      const body = buildLoginBody(username, password);
      const result = await postLogin(body);

      if (!result.ok) {
        return { success: false, error: result.error };
      }

      if (result.mfaRequired && Configs.enableMfaUi) {
        applySession(result.session);
        setMfaPending(true);
        return { success: false };
      }

      if (result.mfaRequired && !Configs.enableMfaUi) {
        return {
          success: false,
          error: {
            code: 'server' as const,
            message: 'Multi-factor authentication is required but not enabled in this build.',
          },
        };
      }

      applySession(result.session);
      setMfaPending(false);
      return { success: true };
    } catch (error) {
      const message =
        error instanceof Error && error.message.includes('VITE_PRIVATE_KEY')
          ? 'Login is not configured (missing VITE_PRIVATE_KEY).'
          : 'Unable to sign in. Please try again.';
      return {
        success: false,
        error: { code: 'misconfigured' as const, message },
      };
    } finally {
      setIsLoading(false);
    }
  }, [applySession]);

  const loginWithGoogleCredential = useCallback(async (credential: KincodeGoogleCredential) => {
    setIsLoading(true);
    setMfaPending(false);

    try {
      const result = await postGoogleSso(credential);

      if (result.ok) {
        applySession(result.session);
        setMfaPending(false);
        return { success: true as const };
      }

      if ('notOnboarded' in result && result.notOnboarded) {
        return { success: false as const, notOnboarded: true as const, message: result.message };
      }

      if ('error' in result) {
        return { success: false as const, error: result.error };
      }

      return {
        success: false as const,
        error: { code: 'server' as const, message: 'Unable to sign in with Google. Please try again.' },
      };
    } catch {
      return {
        success: false as const,
        error: { code: 'server' as const, message: 'Unable to sign in with Google. Please try again.' },
      };
    } finally {
      setIsLoading(false);
    }
  }, [applySession]);

  const loginWithGoogleAccessToken = useCallback(async (accessToken: string) => {
    const credential = buildKincodeGoogleCredential(null, accessToken);
    if (!credential) {
      return {
        success: false as const,
        error: { code: 'server' as const, message: 'Google sign-in did not return a valid credential.' },
      };
    }

    return loginWithGoogleCredential(credential);
  }, [loginWithGoogleCredential]);

  const completeMfaStub = useCallback(() => {
    setMfaPending(false);
  }, []);

  const clearLoginNotice = useCallback(() => {
    setLoginNotice(null);
  }, []);

  const expireSession = useCallback(() => {
    setMfaPending(false);
    applySession(null);
    setLoginNotice(SESSION_EXPIRED_MESSAGE);
  }, [applySession]);

  useEffect(() => {
    if (session != null && isBearerExpired(session.bearerToken)) {
      expireSession();
    }
  }, [expireSession, session]);

  useEffect(() => {
    const validateOnFocus = () => {
      if (clearSessionIfExpired(session)) {
        expireSession();
      }
    };

    window.addEventListener('focus', validateOnFocus);
    return () => window.removeEventListener('focus', validateOnFocus);
  }, [expireSession, session]);

  const logout = useCallback(async () => {
    const currentSession = session;
    setIsLoggingOut(true);

    try {
      if (currentSession) {
        await postLogout(currentSession);
      }
    } finally {
      setMfaPending(false);
      applySession(null);
      setLoginNotice(null);
      setIsLoggingOut(false);
    }
  }, [applySession, session]);

  const value = useMemo<AuthContextValue>(() => {
    const sessionIsValid =
      session !== null && !isBearerExpired(session.bearerToken);

    return {
      session,
      isAuthenticated: sessionIsValid && !mfaPending,
      mfaPending,
      isLoading,
      isLoggingOut,
      loginNotice,
      login,
      loginWithGoogleAccessToken,
      loginWithGoogleCredential,
      completeMfaStub,
      clearLoginNotice,
      expireSession,
      logout,
    };
  }, [
      session,
      mfaPending,
      isLoading,
      isLoggingOut,
      loginNotice,
      login,
      loginWithGoogleAccessToken,
      loginWithGoogleCredential,
      completeMfaStub,
      clearLoginNotice,
      expireSession,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Access auth context (must be used within AuthProvider).
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
